import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import moment from "moment/moment";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Avatar,
  AppBar,
  Toolbar,
  Fade,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  ArrowUpwardTwoTone as ArrowUpwardIcon,
  InsertDriveFile as Document,
  Link as LinkIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import Toast from "../../components/toast";

import { gradientBg, primary } from "../../constants/colors";
import chatBotIcon from "../../assets/chatBotIcon.png";
import Person from "../../assets/Person.svg";

// import Questions from "../data/Questions";
import { emailRegex, dateRegex, fourDigitsRegex } from "../../constants/regex";
import ConsentForm from "../../components/consentForm";
import ReportUpload from "../../components/reportUpload";
import AddMedicationsPopup from "../../components/addMedications";
import AppointmentBooking from "../../components/AppointmentBooking";
import InsuranceUpload from "../../components/insuranceUpload";
import { useCalendlyEventListener } from "react-calendly";
import CheckoutForm from "../../components/checkoutForm";

import {
  getUserOpenChat,
  saveAnswer,
  EditLastAnswer,
  downloadPrescription,
  startNewChat,
} from "../../helpers/UserChats";
import { API_URL } from "../../helpers/api_helper";
import { getFacilities } from "../../helpers/Facilities";

const Chat = () => {
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const User = localStorage.getItem("User");
  const user_ = JSON.parse(User);

  const { isMobile, clickedNewEvaluation, setClickedNewEvaluation } =
    useAppContext();
  const { continuedchatID } = location.state || {};

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [selectedMultiOption, setSelectedMultiOption] = useState([]);
  const [selectedFacilityName, setSelectedFacilityName] =
    useState("select a facility");
  const [chatId, setChatId] = useState(null);
  const [currentMessageKey, setCurrentMessageKey] = useState(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  const [messages, setMessages] = useState([]);
  const [userResponse, setUserResponse] = useState({});
  const [inputMessage, setInputMessage] = useState("");
  const [profilePicture, setProfilePicture] = useState(null);
  const [facilities, setFacilities] = useState([]);

  const [openConsentForm, setOpenConsentForm] = useState(false);
  const [openReportUpload, setOpenReportUpload] = useState(false);
  const [openInsuranceUpload, setOpenInsuranceUpload] = useState(false);
  const [openAddMedication, setOpenAddMedication] = useState(false);
  const [openBookingModal, setOpenBookingModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [isEditClicked, setIsEditClicked] = useState(false);

  useEffect(() => {
    if (
      user_?.UserInfo?.User_Photo_Path !== null &&
      user_?.UserInfo?.User_Photo_Path !== ""
    ) {
      setProfilePicture(API_URL + user_?.UserInfo?.User_Photo_Path);
    } else {
      setProfilePicture(Person);
    }
    GetFacilitiesData();
  }, []);

  const GetFacilitiesData = async () => {
    const response = await getFacilities();
    if (response.statusCode === 200 && response.isSuccess === true) {
      const data = response.data;
      setFacilities(data);
    }
  };

  const getUserOpenChats = async () => {
    const response = await getUserOpenChat(continuedchatID);
    if (response.statusCode === 200 && response.isSuccess === true) {
      const data = response.data;
      setChatId(data._id);
      setCurrentQuestionId(data.currentQuestionId);
      setUserResponse(data.userResponse ?? {});
      setMessages(data.messages ?? []);
      setSelectedFacilityName(data.userResponse[53]);
    } else {
      Toast.error(response.message);
    }
  };

  useEffect(() => {
    if (shouldScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setShouldScroll(false);
    }
  }, [shouldScroll, messages, currentQuestionId]);

  useEffect(() => {
    setShouldScroll(true);
  }, [messages, currentQuestionId]);

  const handleChoiceSelect = (questionId, selectedLabel, selectedValue) => {
    setMessages((prev) => {
      try {
        setIsSubmitting(true);
        // 1️⃣ Update the selectedValue for the message with this questionId
        const updatedMessages = prev.map((msg) =>
          msg.id === questionId
            ? { ...msg, selectedOption: selectedValue } // add/update keys
            : msg,
        );

        const newMessage = {
          id: moment().format("YYYY_MM_DDTHH_mm_ss"),
          sender: "You",
          text: selectedValue,
        };
        const messages_ = updatedMessages;
        messages_.push(newMessage);

        setUserResponse((prev) => ({ ...prev, [questionId]: selectedValue }));
        saveUserAnswer(
          {
            // userID: user_?.UserInfo?.id,
            chatID: chatId,
            questionId: questionId,
            answer: selectedValue,
          },
          messages_,
        );
        return messages_;
      } catch (error) {
        Toast.error("Error saving answer");
        setIsSubmitting(false);
        return prev;
      }
    });
  };

  const handleMultiChoiceSelect = (selectedValue) => {
    setSelectedMultiOption((prev) => {
      try {
        if (prev.includes(selectedValue)) {
          return prev.filter((item) => item !== selectedValue);
        }
        if (prev.length > 0 && selectedValue === "None") {
          Toast.error("Cannot select None with other answers!");
          return prev;
        } else if (prev.includes("None") && selectedValue !== "None") {
          Toast.error("Cannot select None with other answers!");
          return prev;
        }
        return [...prev, selectedValue];
      } catch (error) {
        Toast.error("Error selecting option");
        return prev;
      }
    });
  };

  const handleDropdownSelect = (selectedValue) => {
    setSelectedFacilityName(selectedValue.target.value);
    handleSendMessage(selectedValue.target.value);
  };

  const handleSubmitMultiChoiceSelect = (questionId) => {
    try {
      if (selectedMultiOption.length === 0) {
        Toast.error("Please select at least one option.");
        return;
      }
      setIsSubmitting(true);
      // Save the answer
      const _selectedMultiOption = selectedMultiOption;
      setMessages((prev) => {
        // 1️⃣ Update the selectedValue for the message with this questionId
        const updatedMessages = prev.map((msg) =>
          msg.id === questionId
            ? { ...msg, selectedOption: _selectedMultiOption.join(",") } // add/update keys
            : msg,
        );

        const newMessage = {
          id: moment().format("YYYY_MM_DDTHH_mm_ss"),
          sender: "You",
          text: _selectedMultiOption.join(","),
        };
        const messages_ = updatedMessages;
        messages_.push(newMessage);

        setUserResponse((prev) => ({
          ...prev,
          [questionId]: _selectedMultiOption.join(","),
        }));
        setSelectedMultiOption([]);
        saveUserAnswer(
          {
            // userID: user_?.UserInfo?.id,
            chatID: chatId,
            questionId: questionId,
            answer: _selectedMultiOption.join(","),
          },
          messages_,
        );
        setIsSubmitting(false);
        return messages_;
      });
    } catch (error) {
      Toast.error("Error saving answer");
      setIsSubmitting(false);
    }
  };

  const handleSendMessage = (_inputMessage = inputMessage) => {
    try {
      setIsSubmitting(true);
      const messages_ = messages;
      const currentMessage = messages_[messages_.length - 1];
      if (
        currentMessage?.key === "email" &&
        !emailRegex.test(_inputMessage.trim())
      ) {
        Toast.error("Please enter a valid email address.");
        setIsSubmitting(false);
        return;
      } else if (
        currentMessage?.key === "date" &&
        !dateRegex.test(_inputMessage.trim())
      ) {
        Toast.error("Date must be in MM/DD/YYYY format");
        setIsSubmitting(false);
        return;
      } else if (
        currentMessage?.key === "snn" &&
        !fourDigitsRegex.test(_inputMessage.trim())
      ) {
        Toast.error("SSN must be four digits.");
        setIsSubmitting(false);
        return;
      }

      if (_inputMessage.trim()) {
        const newMessage = {
          id: moment().format("YYYY_MM_DDTHH_mm_ss"),
          sender: "You",
          text: _inputMessage.trim(),
        };

        messages_.push(newMessage);
        setMessages(messages_);
        setInputMessage("");
        setUserResponse((prev) => ({
          ...prev,
          [currentQuestionId]: _inputMessage.trim(),
        }));
        saveUserAnswer(
          {
            // userID: user_?.UserInfo?.id,
            chatID: chatId,
            questionId: currentQuestionId,
            answer: _inputMessage.trim(),
          },
          messages_,
        );
        setIsSubmitting(false);
      } else {
        Toast.error("Please enter an answer.");
        setIsSubmitting(false);
      }
    } catch (error) {
      Toast.error("Error saving answer");
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const messages_ = messages;
    const currentMessage = messages_[messages_.length - 1];
    if (
      messages_.length > 0 &&
      currentMessage?.sender === "Medical Assistant" &&
      currentMessage?.type === "norply"
    ) {
      saveUserAnswer({
        // userID: user_?.UserInfo?.id,
        chatID: chatId,
        questionId: currentQuestionId,
      });
    }
  }, [messages, currentQuestionId]);

  useEffect(() => {
    if (clickedNewEvaluation === true) {
      setClickedNewEvaluation(false);
      startUserNewChat();
    } else {
      getUserOpenChats();
    }
  }, [clickedNewEvaluation, continuedchatID]);

  const startUserNewChat = async () => {
    const response = await startNewChat();
    if (response.statusCode === 200 && response.isSuccess === true) {
      const data = response.data;
      setChatId(data._id);
      setCurrentQuestionId(data.currentQuestionId);
      setUserResponse(data.userResponse ?? {});
      setMessages(data.messages ?? []);
    } else {
      Toast.error(response.message);
    }
  };

  const saveUserAnswer = async (payload, messages_ = messages) => {
    try {
      setIsSubmitting(true);
      const response = await saveAnswer(payload);
      if (response.statusCode === 200 && response.isSuccess === true) {
        messages_.push(response.data);
        setMessages(messages_);
        setCurrentQuestionId(response.data.id);
        setInputMessage("");
        setIsEditClicked(false);

        // to focus
        if (response.data?.type === "text") {
          // Small timeout ensures DOM is updated
          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        }
      } else {
        Toast.error(response.message);
      }
      setIsSubmitting(false);
    } catch (error) {
      Toast.error("Error saving answer");
      setIsSubmitting(false);
    }
  };

  const handleSignConsentForm = async () => {
    handleSendMessage("The consent form has been signed.");
    setOpenConsentForm(false);
  };

  const handleUploadReport = async (isUploaded) => {
    if (isUploaded) {
      setOpenReportUpload(false);
      handleSendMessage("I have successfully uploaded the reports.");
    } else {
      Toast.error("Unable to Upload Report.");
    }
  };

  const handleUploadInsurance = async (isUploaded) => {
    if (isUploaded) {
      setOpenInsuranceUpload(false);
      handleSendMessage("I have successfully uploaded the insurance card.");
    } else {
      Toast.error("Unable to Upload Report.");
    }
  };

  const handleDownloadPrescription = async () => {
    try {
      setIsLoading(true);
      const response = await downloadPrescription({
        chatID: chatId,
      });
      handleSendMessage("Downloaded now.");
      setIsLoading(false);
    } catch (error) {
      Toast.error("Unable to Download Prescription.");
      setIsLoading(false);
    }
  };

  const handleAddMedications = async (isAdded) => {
    if (isAdded) {
      setOpenAddMedication(false);
      handleSendMessage("I have added medications.");
    } else {
      Toast.error("Unable to Upload Report.");
    }
  };

  const handleBookAppointmentSuccess = () => {
    setOpenBookingModal(false);
    handleSendMessage("Appointment booked successfully.");
  };

  const handleEdit = async (index, message) => {
    try {
      setIsEditClicked(true);
      const answeredQuestion = messages[index - 1];

      const editResponse = await EditLastAnswer({
        chatID: chatId,
        questionId: answeredQuestion.id,
      });
      if (editResponse.statusCode !== 200 && editResponse.isSuccess !== true) {
        Toast.error(editResponse.message);
        return;
      }

      // If the question type is text
      if (answeredQuestion.type === "text") {
        const newSlicedArray = messages.slice(0, index);
        setMessages(newSlicedArray);
        setCurrentQuestionId(answeredQuestion.id);
        setInputMessage(message);
      } else if (
        answeredQuestion.type === "choice" ||
        answeredQuestion.type === "multi_choice"
      ) {
        const newSlicedArray = messages.slice(0, index);
        newSlicedArray[newSlicedArray.length - 1] = {
          ...newSlicedArray[newSlicedArray.length - 1],
          selectedOption: "",
        };
        setMessages(newSlicedArray);
        setCurrentQuestionId(answeredQuestion.id);
      }
    } catch (error) {
      Toast.error(error);
    }
  };

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      handleSendMessage("Appointment booked successfully.");
      //setIsBooked(true); // Switch view to success
    },
  });

  const lastAssistantMessage = [...messages]
    .reverse()
    .find((m) => m.sender === "Medical Assistant");

  const isInputDisabled =
    messages.length > 0 &&
    !!lastAssistantMessage &&
    (lastAssistantMessage.type !== "text" ||
      lastAssistantMessage.type === "choice" ||
      lastAssistantMessage.type === "norply" ||
      lastAssistantMessage.next === 0);

      console.log("isInputDisabled", isInputDisabled);
      console.log("lastAssistantMessage", lastAssistantMessage);

  useEffect(() => {
    if (!isInputDisabled) {
      const focusInput = () => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      };

      setTimeout(focusInput, 0);
      setTimeout(focusInput, 30);
      setTimeout(focusInput, 80);
    }
  }, [isInputDisabled]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        // background: gradientBg,
        height: isMobile ? "calc(100vh - 40px)" : "calc(100vh - 65px)",
        paddingTop: isMobile ? "40px" : "0px",
        overflow: "hidden",
        maxWidth: "1000px",
        margin: "0 auto",
      }}
    >
      <ConsentForm
        open={openConsentForm}
        handleClose={() => setOpenConsentForm(false)}
        handleSignConsentForm={handleSignConsentForm}
        initialData={{ ...userResponse, chatId: chatId }}
      />
      <ReportUpload
        open={openReportUpload}
        handleClose={() => setOpenReportUpload(false)}
        handleUploadReport={handleUploadReport}
        chatId={chatId}
      />
      <InsuranceUpload
        open={openInsuranceUpload}
        handleClose={() => setOpenInsuranceUpload(false)}
        handleUpload={handleUploadInsurance}
        chatId={chatId}
        currentMessageKey={currentMessageKey}
      />
      <AddMedicationsPopup
        open={openAddMedication}
        handleClose={() => setOpenAddMedication(false)}
        handleAddMedications={handleAddMedications}
        chatId={chatId}
      />
      <AppointmentBooking
        open={openBookingModal}
        handleClose={() => setOpenBookingModal(false)}
        handleBookAppointmentSuccess={handleBookAppointmentSuccess}
        chatId={chatId}
      />
      <CheckoutForm
        open={openPaymentModal}
        handleClose={() => setOpenPaymentModal(false)}
        handlePaymentSuccess={() => {
          setOpenPaymentModal(false);
          handleSendMessage("Payment done.");
        }}
        chatId={chatId}
      />
      {/* Messages Container */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
          gap: 2,
        }}
      >
        {messages.map((message, index) => (
          <Fade key={index} in timeout={300}>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexDirection:
                  message.sender === "Medical Assistant"
                    ? "row"
                    : "row-reverse",
                alignItems: "flex-start",
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor:
                    message.sender === "Medical Assistant"
                      ? "#8b5cf6"
                      : "rgba(255,255,255,0.2)",
                  flexShrink: 0,
                  marginTop: "5px",
                }}
              >
                {message.sender === "Medical Assistant" ? (
                  <img src={chatBotIcon} width={32} height={32} />
                ) : (
                  <img src={profilePicture} width={32} height={32} />
                )}
              </Avatar>
              <Paper
                elevation={0}
                className="chat-message"
                sx={{
                  maxWidth: { xs: "85%", sm: "70%" },
                  backgroundColor: "transparent",
                  color: "#202020",
                  padding: "0px 5px",
                  borderRadius: "12px",
                  whiteSpace: "pre-wrap",
                  textAlign:
                    message.sender === "Medical Assistant" ? "left" : "right",
                }}
              >
                <Typography variant="body1" className="sf-bold sf-bold--dark">
                  {message.sender}
                </Typography>
                <div sx={{ mb: message.type === "choice" ? 1 : 0 }}>
                  <Typography
                    variant="body1"
                    dangerouslySetInnerHTML={{
                      __html:
                        userResponse[2] && userResponse[2] === "self"
                          ? message.text
                              .replaceAll("the Resident's ", "your ")
                              .replaceAll("Does the resident's ", "Does your ")
                              .replaceAll("Does the resident ", "Do you ")
                              .replaceAll("does the resident ", "do you ")
                              .replaceAll("Is the resident ", "Are you ")
                              .replaceAll("is the resident ", "are you ")
                              .replaceAll("is resident ", "are you ")
                              .replaceAll("The resident is", "You are")
                              .replaceAll("the resident is", "you are")
                              .replaceAll("the resident has", "you have")
                              .replaceAll("the resident's ", "your ")
                              .replaceAll("the resident ", "you ")
                              .replaceAll(" their ", " your ")
                          : message.text,
                    }}
                    className="sf-regular sf-regular--dark"
                  />
                  {/* Edit Functionality */}
                  {message.sender === "You" &&
                    index === messages.length - 2 &&
                    isEditClicked === false && (
                      <IconButton
                        className="edit-button"
                        style={{
                          // width: "20px",
                          margin: 0,
                          padding: 0,
                          // backgroundColor: "transparent",
                        }}
                        disabled={isLoading}
                        onClick={(e) => handleEdit(index, message.text)}
                      >
                        <EditNoteOutlinedIcon
                          sx={{
                            fontSize: 24,
                            margin: 0,
                            padding: 0,
                            border: 0,
                          }}
                          className="color-primary"
                        />
                      </IconButton>
                    )}
                </div>
                {message.type === "choice" && message.options ? (
                  <Box
                    sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}
                  >
                    {message.options.map((option, idx) => {
                      const _label =
                        userResponse[2] && userResponse[2] !== "self"
                          ? option.label
                              .replaceAll("I need", "The resident needs")
                              .replaceAll("I am", "The resident is")
                              .replaceAll("I have", "The resident has")
                              .replaceAll("you ", "resident ")
                              .replaceAll("my ", "their ")
                          : option.label;
                      // const _value =
                      //   userResponse[2] && userResponse[2] !== "self"
                      //     ? option.value
                      //         .replace("I need", "Redisdent needs")
                      //         .replace("I am", "Resident is")
                      //     : option.value;
                      return (
                        <Button
                          key={idx}
                          className={
                            message.selectedOption === option.value
                              ? "sf-bold color-light background-primary"
                              : "sf-bold color-primary"
                          }
                          variant="outlined"
                          size="small"
                          sx={{
                            borderColor: primary,
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "5px 20px",
                            cursor:
                              message.id === currentQuestionId &&
                              isSubmitting === false
                                ? "pointer"
                                : "default",
                          }}
                          onClick={() => {
                            if (
                              message.id === currentQuestionId &&
                              isSubmitting === false
                            ) {
                              handleChoiceSelect(
                                message.id,
                                _label,
                                option.value,
                              );
                            }
                          }}
                        >
                          {_label}
                        </Button>
                      );
                    })}
                  </Box>
                ) : message.type === "multi_choice" && message.options ? (
                  <>
                    <Box
                      sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}
                    >
                      {message.options.map((option, idx) => {
                        const _label =
                          userResponse[2] && userResponse[2] !== "self"
                            ? option.label
                                .replaceAll("I need", "Redisdent needs")
                                .replaceAll("I am", "Resident is")
                                .replaceAll("I have", "Resident has")
                                .replaceAll("you ", "resident ")
                                .replaceAll("my ", "their ")
                            : option.label;
                        // const _value =
                        //   userResponse[2] && userResponse[2] !== "self"
                        //     ? option.value
                        //         .replace("I need", "Redisdent needs")
                        //         .replace("I am", "Resident is")
                        //     : option.value;
                        return (
                          <Button
                            key={idx}
                            className={
                              message.selectedOption !== null &&
                              message.selectedOption !== ""
                                ? message.selectedOption
                                    .split(",")
                                    .includes(option.value)
                                  ? "sf-bold color-light background-primary"
                                  : "sf-bold color-primary"
                                : selectedMultiOption.includes(option.value)
                                  ? "sf-bold color-light background-primary"
                                  : "sf-bold color-primary"
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                              borderColor: primary,
                              textTransform: "none",
                              borderRadius: "8px",
                              padding: "5px 20px",
                              cursor:
                                message.id === currentQuestionId &&
                                isSubmitting === false
                                  ? "pointer"
                                  : "default",
                            }}
                            onClick={() => {
                              if (
                                message.id === currentQuestionId &&
                                isSubmitting === false
                              ) {
                                handleMultiChoiceSelect(option.value);
                              }
                            }}
                          >
                            {_label}
                          </Button>
                        );
                      })}
                    </Box>
                    <Box
                      sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}
                    >
                      <Button
                        className={
                          // message.selectedOption === option.value
                          "sf-bold color-light background-primary"
                          // "sf-bold color-primary"
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          borderColor: primary,
                          textTransform: "none",
                          borderRadius: "8px",
                          padding: "5px 20px",
                          cursor:
                            message.id === currentQuestionId &&
                            isSubmitting === false
                              ? "pointer"
                              : "default",
                        }}
                        onClick={() => {
                          if (
                            message.id === currentQuestionId &&
                            isSubmitting === false
                          ) {
                            handleSubmitMultiChoiceSelect(message.id);
                          }
                        }}
                      >
                        SUBMIT
                      </Button>
                    </Box>
                  </>
                ) : message.type === "select_option" ? (
                  <Box
                    sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}
                  >
                    <FormControl fullWidth size="small">
                      {/* <InputLabel id="demo-simple-select-label">
                        Facility
                      </InputLabel> */}
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={selectedFacilityName}
                        // label="Facility"
                        displayEmpty
                        renderValue={(selected) =>
                          selected && selected !== "select a facility"
                            ? selected
                            : "Select a facility"
                        }
                        onChange={(e) => {
                          if (
                            message.id === currentQuestionId &&
                            isSubmitting === false
                          ) {
                            handleDropdownSelect(e);
                          }
                        }}
                        disabled={message.id !== currentQuestionId}
                      >
                        {facilities.map((fa) => (
                          <MenuItem
                            key={fa.facilityName}
                            value={fa.facilityName}
                          >
                            {fa.facilityName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : // <Box
                //   sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}
                // >
                //   <Select
                //     fullWidth
                //     labelId="demo-simple-select-label"
                //     id="demo-simple-select"
                //     value={selectedFacilityName}
                //     label="Facility"
                //     size="small"
                //     fullWidth
                //     placeholder="Select facility"
                //     onChange={(e) => {
                //       if (
                //         message.id === currentQuestionId &&
                //         isSubmitting === false
                //       ) {
                //         handleDropdownSelect(e);
                //       }
                //     }}
                //     disabled={message.id !== currentQuestionId}
                //   >
                //     {facilities.map((fa) => (
                //       <MenuItem value={fa.facilityName}>
                //         {fa.facilityName}
                //       </MenuItem>
                //     ))}
                //   </Select>
                // </Box>
                message.type === "consent" ? (
                  <Box
                    className="background-dark"
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      width: "300px",
                      mt: 2,
                      p: 2,
                      borderRadius: { xs: "13px", sm: "13px" },
                      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                      }}
                    >
                      <Document
                        sx={{ mx: 1, fontSize: 36 }}
                        className="color-light"
                      />
                      <Box>
                        <Typography
                          sx={{ fontSize: "18px" }}
                          className="sf-bold sf-bold--light"
                        >
                          Telemedical PME Consent
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px" }}
                          className="sf-regular sf-regular--light"
                        >
                          Document
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      className="sf-medium sf-medium--light background-primary"
                      sx={{
                        width: "100%",
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        // if (
                        //   message.id === currentQuestionId &&
                        //   isSubmitting === false
                        // ) {
                        setOpenConsentForm(true);
                        // }
                      }}
                    >
                      Sign this form
                    </Button>
                  </Box>
                ) : message.type === "presecriptionDownload" ? (
                  <Box
                    className="background-dark"
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      width: "300px",
                      mt: 2,
                      p: 2,
                      borderRadius: { xs: "13px", sm: "13px" },
                      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                      }}
                    >
                      {/* <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          mx: 1,
                        }}
                      > */}
                      <Document
                        sx={{ mx: 1, fontSize: 36 }}
                        className="color-light"
                      />
                      {/* </Avatar> */}
                      <Box>
                        <Typography
                          sx={{ fontSize: "18px" }}
                          className="sf-bold sf-bold--light"
                        >
                          Prescription
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px" }}
                          className="sf-regular sf-regular--light"
                        >
                          Document
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      className="sf-medium sf-medium--light background-primary"
                      sx={{
                        width: "100%",
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (
                          message.id === currentQuestionId &&
                          isSubmitting === false
                        ) {
                          handleDownloadPrescription();
                        }
                      }}
                    >
                      {isLoading === true ? (
                        <CircularProgress color="#5855eb" size={"22px"} />
                      ) : (
                        "Download"
                      )}
                    </Button>
                  </Box>
                ) : message.type === "reportUpload" ? (
                  <Box
                    className="background-dark"
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      width: "300px",
                      mt: 2,
                      p: 2,
                      borderRadius: { xs: "13px", sm: "13px" },
                      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                      }}
                    >
                      <Document
                        sx={{ mx: 1, fontSize: 36 }}
                        className="color-light"
                      />
                      <Box>
                        <Typography
                          sx={{ fontSize: "18px" }}
                          className="sf-bold sf-bold--light"
                        >
                          Upload Your Reports
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px" }}
                          className="sf-regular sf-regular--light"
                        >
                          Document
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      className="sf-medium sf-medium--light background-primary"
                      sx={{
                        width: "100%",
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (
                          message.id === currentQuestionId &&
                          isSubmitting === false
                        ) {
                          setOpenReportUpload(true);
                        }
                      }}
                    >
                      Upload
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        color: "white",
                        width: "100%",
                        backgroundColor: "transparent",
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (
                          message.id === currentQuestionId &&
                          isSubmitting === false
                        ) {
                          handleSendMessage("I'll Upload the report later.");
                        }
                      }}
                    >
                      Upload Later
                    </Button>
                  </Box>
                ) : message.type === "medications" ? (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      mt: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      className="sf-medium sf-medium--light background-primary"
                      sx={{
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (
                          message.id === currentQuestionId &&
                          isSubmitting === false
                        ) {
                          setOpenAddMedication(true);
                        }
                      }}
                    >
                      Add Medications
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      className="sf-medium sf-medium--light background-primary"
                      sx={{
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (
                          message.id === currentQuestionId &&
                          isSubmitting === false
                        ) {
                          handleSendMessage(
                            "I don't have any medications to add.",
                          );
                        }
                      }}
                    >
                      No Medications
                    </Button>
                  </Box>
                ) : message.type === "insurance_upload" ? (
                  <Box
                    className="background-dark"
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      width: "300px",
                      mt: 2,
                      p: 2,
                      borderRadius: { xs: "13px", sm: "13px" },
                      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                      }}
                    >
                      <Document
                        sx={{ mx: 1, fontSize: 36 }}
                        className="color-light"
                      />
                      <Box>
                        <Typography
                          sx={{ fontSize: "18px" }}
                          className="sf-bold sf-bold--light"
                        >
                          Upload Your Insurance Card
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px" }}
                          className="sf-regular sf-regular--light"
                        >
                          Document
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      className="sf-medium sf-medium--light background-primary"
                      sx={{
                        width: "100%",
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (
                          message.id === currentQuestionId &&
                          isSubmitting === false
                        ) {
                          setCurrentMessageKey(message.key);
                          setOpenInsuranceUpload(true);
                        }
                      }}
                    >
                      Upload
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        color: "white",
                        width: "100%",
                        backgroundColor: "transparent",
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (
                          message.id === currentQuestionId &&
                          isSubmitting === false
                        ) {
                          handleSendMessage("I'll Upload later.");
                        }
                      }}
                    >
                      Upload Later
                    </Button>
                  </Box>
                ) : message.type === "book_appointment" ? (
                  <Box
                    className="background-dark"
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      width: "300px",
                      mt: 2,
                      p: 2,
                      borderRadius: "13px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                      }}
                    >
                      <LinkIcon
                        sx={{ mx: 1, fontSize: 36 }}
                        className="color-light"
                      />
                      <Box>
                        <Typography
                          sx={{ fontSize: "18px" }}
                          className="sf-bold sf-bold--light"
                        >
                          Book your appointment
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px" }}
                          className="sf-regular sf-regular--light"
                        >
                          link
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      className="sf-medium sf-medium--light background-primary"
                      sx={{
                        width: "100%",
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (
                          message.id === currentQuestionId &&
                          isSubmitting === false
                        ) {
                          setOpenBookingModal(true);
                        }
                      }}
                    >
                      Book Now
                    </Button>
                  </Box>
                ) : message.type === "payment_gateway" ? (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      mt: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="large"
                      className="sf-medium sf-medium--light background-primary"
                      sx={{
                        borderColor: "#8b5cf6",
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 20px",
                        cursor:
                          message.id === currentQuestionId &&
                          isSubmitting === false
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (
                          message.id === currentQuestionId &&
                          isSubmitting === false
                        ) {
                          setOpenPaymentModal(true);
                        }
                      }}
                    >
                      {`Pay Your Fee - $${message.key}`}
                    </Button>
                  </Box>
                ) : (
                  // <Box
                  //   className="background-dark"
                  //   sx={{
                  //     display: "flex",
                  //     gap: 1,
                  //     flexWrap: "wrap",
                  //     width: "300px",
                  //     mt: 2,
                  //     p: 2,
                  //     borderRadius: { xs: "13px", sm: "13px" },
                  //     boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                  //   }}
                  // >
                  //   <Box
                  //     sx={{
                  //       display: "flex",
                  //     }}
                  //   >
                  //     <Document
                  //       sx={{ mx: 1, fontSize: 36 }}
                  //       className="color-light"
                  //     />
                  //     <Box>
                  //       <Typography
                  //         sx={{ fontSize: "18px" }}
                  //         className="sf-bold sf-bold--light"
                  //       >
                  //         Telemedical PME Consent
                  //       </Typography>
                  //       <Typography
                  //         sx={{ fontSize: "12px" }}
                  //         className="sf-regular sf-regular--light"
                  //       >
                  //         Document
                  //       </Typography>
                  //     </Box>
                  //   </Box>
                  //   <Button
                  //     variant="outlined"
                  //     size="small"
                  //     className="sf-medium sf-medium--light background-primary"
                  //     sx={{
                  //       width: "100%",
                  //       borderColor: "#8b5cf6",
                  //       textTransform: "none",
                  //       borderRadius: "8px",
                  //       padding: "5px 20px",
                  //       cursor:
                  //         message.id === currentQuestionId &&
                  //         isSubmitting === false
                  //           ? "pointer"
                  //           : "default",
                  //     }}
                  //     onClick={() => {
                  //       // if (
                  //       //   message.id === currentQuestionId &&
                  //       //   isSubmitting === false
                  //       // ) {
                  //         setOpenPaymentModal(true);
                  //       // }
                  //     }}
                  //   >
                  //     {`Pay Your Fee - $${message.key}`}
                  //   </Button>
                  // </Box>
                  ""
                )}
              </Paper>
            </Box>
          </Fade>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Message Input */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "90%",
            backgroundColor: "transparent",
            backdropFilter: "blur(10px)",
            p: 1,
          }}
        >
          <TextField
            fullWidth
            placeholder="Message"
            // disabled={
            //   messages.length > 0 &&
            //   (messages[messages.length - 1].type !== "text" ||
            //     messages[messages.length - 1].type === "choice" ||
            //     messages[messages.length - 1].type === "norply" ||
            //     messages[messages.length - 1].next === 0)
            // }
            disabled={isInputDisabled}
            inputRef={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => {
              if (
                e.key === "Enter" &&
                inputMessage.length > 0 &&
                !isSubmitting
              ) {
                setShouldScroll(true);
                handleSendMessage();
              }
            }}
            inputProps={{
              autoComplete: "off",
              style: {
                WebkitBoxShadow: "0 0 0 1000px #F1F2FF inset",
                WebkitTextFillColor: "#202020",
                caretColor: "#202020",
                border: "none", // ← Add this
                outline: "none", // ← Add this too
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F1F2FF",
                color: "#202020",
                minHeight: "45px",
                "& fieldset": { borderColor: "#F1F2FF" },
                "&:hover fieldset": { borderColor: "#ccc" },
                "&.Mui-focused fieldset": { borderColor: "#ccc" },
              },
              "& .MuiInputLabel-root": {
                color: "#202020 !important",
                "&.Mui-focused": {
                  color: "#202020 !important",
                },
                "&.MuiInputLabel-shrink": {
                  color: "#202020 !important",
                },
              },
              "& .MuiInputBase-input": {
                color: "#202020",
                backgroundColor: "#F1F2FF !important",
                border: "none !important", // ← Add this for extra safety
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#F1F2FF",
                opacity: 1,
              },
            }}
          />
        </Paper>
        <IconButton
          onClick={() => {
            if (inputMessage.length > 0 && !isSubmitting) {
              setShouldScroll(true);
              handleSendMessage();
            }
          }}
          disabled={!inputMessage.trim() || isSubmitting}
          className="color-primary"
          sx={{
            width: isMobile ? 45 : 45,
            height: isMobile ? 45 : 45,
            border: `2px solid ${primary}`, // white border
            "&:hover": {
              transform: "scale(1.05)",
            },
            "&:disabled": {
              color: "#aaa",
              border: `2px solid ${primary}`, // white border
            },
          }}
        >
          <ArrowUpwardIcon className="color-primary" style={{ fontSize: 32 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Chat;
